/**
 * 转账验证码页面
 * 支持邮箱验证码和谷歌验证器两种验证方式
 */

import React, { useState, useEffect, useMemo, useCallback } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Toast } from 'react-vant'
import PageLayout from '@/components/PageLayout'
import Header from '@/components/Header'
import Back from '@/components/Back'
import VerificationCodeInput from '@/components/VerificationCodeInput'
import CustomTextInput from '@/components/CustomTextInput'
import CustomButton from '@/components/CustomButton'
import { useSendCode } from '@/hooks/useSendCode'
import { APIWeb } from '@/api'
import { useTransactionRefresh } from '@/hooks'
import { VerActionType } from '@/components/SelectVerification'

/**
 * 路由状态参数
 */
interface VerifyCodeState {
  /** 转账参数 */
  transferData: {
    amount: string
    pay_type: string
    target_user_info: string
    target_user_type: string
    remark?: string
  }
  /** 用于跳转成功页面的数据 */
  successData: {
    currency: string
    currencyIcon: string
    amount: string
    recipientType: string
    recipientValue: string
    fee: string
  }
  /** 验证方式 */
  verification: VerActionType
  /** 账户（邮箱，谷歌验证时为空） */
  account: string
}

export default React.memo(function TransferVerifyCode() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const { refreshAfterTransaction } = useTransactionRefresh()
  const { sendCode, sendCodeLoading } = useSendCode()

  const [submitLoading, setSubmitLoading] = useState(false)
  const [googleCode, setGoogleCode] = useState('')

  // 从路由状态中获取参数
  const state = location.state as VerifyCodeState | null
  const verification = state?.verification
  const account = state?.account || ''

  // 是否使用谷歌验证器
  const isGoogleVerification = verification === VerActionType.GoogleVer

  // 如果没有传递必要参数，返回上一页
  useEffect(() => {
    if (!state || !state.transferData) {
      console.error('缺少转账参数')
      Toast.fail(t('参数错误'))
      navigate(-1)
      return
    }
  }, [])

  // 重新发送验证码（仅邮箱验证时使用）
  const handleSendCode = useCallback(async () => {
    if (!account) {
      Toast.fail(t('请先绑定邮箱'))
      return
    }

    await sendCode({
      account,
      scene: 'balance_transfer',
    })
  }, [account, sendCode, t])

  // 处理谷歌验证码输入
  const handleGoogleCodeChange = useCallback((value: string) => {
    setGoogleCode(value)
  }, [])

  // 处理验证码提交（邮箱和谷歌验证器通用）
  const handleSubmit = useCallback(async (code: string) => {
    if (!state) return

    setSubmitLoading(true)
    try {
      // 第一步：验证验证码并获取 verify_key
      const verifyResult = await APIWeb.verifyOldAccountCode({
        account: isGoogleVerification ? 'google' : account,
        scene: 'balance_transfer',
        code,
      })

      const finalVerifyKey = verifyResult?.verify_key || code

      // 第二步：调用转账API
      await APIWeb.balanceTransfer({
        ...state.transferData,
        verify_key: finalVerifyKey,
      })

      Toast.success({
        message: t('转账成功'),
        duration: 1500,
        onClose: async () => {
          // 刷新状态
          await refreshAfterTransaction()
          // 跳转到成功页面
          navigate('/transfer/success', {
            state: {
              ...state.successData,
              timestamp: new Date().toISOString()
            },
            replace: true
          })
        }
      })
    } catch (error: any) {
      console.error('转账失败:', error)
    } finally {
      setSubmitLoading(false)
    }
  }, [state, account, isGoogleVerification, refreshAfterTransaction, navigate, t])

  // 谷歌验证器输入组件
  const googleInputVer = useMemo(() => {
    return (
      <div>
        <div className="space-y-2">
          <div className="text-3 text-[#1A1A1A]/60 mb-1">{t('请输入谷歌验证码')}</div>
          <CustomTextInput
            value={googleCode}
            onChangeText={handleGoogleCodeChange}
            placeholder={t('请输入谷歌验证码')}
          />
        </div>
        <div className="mt-6">
          <CustomButton
            loading={submitLoading}
            disabled={submitLoading || !googleCode}
            onClick={() => handleSubmit(googleCode)}
          >
            {t('确认')}
          </CustomButton>
        </div>
      </div>
    )
  }, [googleCode, submitLoading, handleGoogleCodeChange, handleSubmit, t])

  if (!state) {
    return null
  }

  return (
    <PageLayout tabbarPage={false}>
      <Header
        leftChildren={<Back />}
        centerChildren={
          <div className="text-3.75 font-medium text-black">
            {t('安全验证')}
          </div>
        }
      />

      <div className="px-6 py-6">
        {isGoogleVerification ? (
          googleInputVer
        ) : (
          <VerificationCodeInput
            account={account}
            onSubmit={handleSubmit}
            onResendCode={handleSendCode}
            submitLoading={submitLoading}
            resendLoading={sendCodeLoading}
            submitButtonText={t('确认')}
            title={t('我们刚刚向您发送了验证码')}
            description={t('请输入我们发送至X的安全代码该代码将在10分钟后失效', { 
              account 
            })}
          />
        )}
      </div>
    </PageLayout>
  )
})
